import axios, {
  type AxiosError,
  type AxiosInstance,
  type InternalAxiosRequestConfig,
} from 'axios';
import { env } from './env';
import { useAuthStore } from '@/stores/auth.store';
import type { AuthTokens, RefreshResponse } from '@/types/auth';
import type { ApiEnvelope } from '@/types/api';

export const http: AxiosInstance = axios.create({
  baseURL: env.API_URL,
  headers: { 'Content-Type': 'application/json' },
});

// ─── Request: inject Bearer token ──────────────────────────────────────────
http.interceptors.request.use((config) => {
  const token = useAuthStore.getState().accessToken;
  if (token && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// ─── Response: single-flight refresh-on-401 ────────────────────────────────
let refreshPromise: Promise<string | null> | null = null;

interface RetriableConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
}

/** Refresh once; concurrent 401s share the same in-flight promise. */
async function refreshAccessToken(): Promise<string | null> {
  if (refreshPromise) return refreshPromise;

  const { refreshToken, setTokens, logout } = useAuthStore.getState();
  if (!refreshToken) {
    logout();
    return null;
  }

  refreshPromise = (async () => {
    try {
      // Bare axios — bypasses interceptors to avoid a refresh loop.
      const res = await axios.post<ApiEnvelope<RefreshResponse>>(
        `${env.API_URL}/auth/refresh`,
        { refreshToken },
        { headers: { 'Content-Type': 'application/json' } },
      );
      const body = res.data;
      if (!body?.status) throw new Error('refresh rejected');

      const d = body.data;
      const tokens: AuthTokens = d.tokens ?? {
        accessToken: d.accessToken ?? '',
        refreshToken: d.refreshToken ?? refreshToken,
        expiresIn: d.expiresIn,
      };
      if (!tokens.accessToken) throw new Error('no access token in refresh');
      setTokens(tokens);
      return tokens.accessToken;
    } catch {
      logout();
      return null;
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

http.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const original = error.config as RetriableConfig | undefined;
    const status = error.response?.status;
    const isRefreshCall = original?.url?.includes('/auth/refresh');

    if (status === 401 && original && !original._retry && !isRefreshCall) {
      original._retry = true;
      const newToken = await refreshAccessToken();
      if (newToken) {
        original.headers.Authorization = `Bearer ${newToken}`;
        return http(original);
      }
      // Refresh failed — fall through; the app's RequireAuth bounces to /login.
    }

    return Promise.reject(error);
  },
);
